import { reaction } from 'mobx';
import { applySnapshot, flow, getRoot, getSnapshot } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';

import { logger } from '@lusha/client-logger';
import { PLATFORM, SUB_PLATFORM } from '@lusha/source-platforms';
import { communicator } from 'lusha-widgets';

import { showContactsByDataPoints } from '../../../API/bulkApi';
import { acquireContacts } from '../../../API/contactAcquireAPI';
import { refreshContactData } from '../../../API/contactRefreshApi';
import { contactDataPointSearch } from '../../../API/contactSearchAPI';
import { sendClientMetric } from '../../../API/monitorAPI';
import { mapSingleSearchData } from '../../../API/search-v2-mappers';
import { saveContactToWishlist } from '../../../API/wishlistApi';
import { USE_BULK_KEY } from '../../../components/Pricing295UnlimitedExceededPopup';
import { BUTTON_TYPES } from '../../../components/ShowContactByDataPoint/constants';
import { getVersionedSelectors } from '../../../hooks/useSearchDataParser/pageListeners';
import { reportDncContactViewedClientEvent } from '../../../utils/BIEvents/dnc-event';
import { RATE_MODAL_TRIGGER_ACTIONS } from '../../../models/UserActivity/constants';
import reportClientEvent from '../../../utils/BIEvents/reportClientEvent';
import { SEARCH_RESULTS, SEARCH_STATUS } from '../../../utils/constants';
import { buildOnDemandShowClientMetricPayload } from '../../../utils/onDemand';
import { sumNumberOfCreditsCharge } from '../../../utils/showHelper';
import { BULK_SEARCH_PLATFORM } from '../../../views/BulkEnrich/helpers';
import { getContactWishlistData } from '../../../views/Wishlist/Utils';
import { getRevenueRangeString } from '../../Company/Actions/utils';
import { SAVE_CONTACT_SUB_PLATFORMS } from '../../UserSettings/constants';
import {
  ACQUIRE_STATUS,
  OUT_OF_CREDITS_BULK_ERROR_MESSAGE,
  OUT_OF_CREDITS_BULK_ERROR_OBJECT,
} from '../constants';

import { setPlatformByParserSourcePlatform } from './utils';

const MAX_SEARCH_TRIES = 2;
let searchTries = 0;

const EMAIL_TYPE_TO_LABEL = {
  work: 'Work',
  direct: 'Direct',
  email: 'Email',
};

function getParsedData(parserResult, contact) {
  const { ordinalId } = contact;
  const { parsedData } = JSON.parse(parserResult);

  if (ordinalId >= 0) {
    const contactParsedData = parsedData.contacts[ordinalId];

    return {
      parsedFullName: contactParsedData.fullName,
      parsedCompanyName: contactParsedData.currentPositions?.[0]?.companyName || null,
      parsedLinkedinUrl: contactParsedData.socialLink,
    };
  }

  return {
    parsedFullName: parsedData.full_name,
    parsedCompanyName: parsedData.organizations?.[0]?.name || parsedData.top_company || null,
    parsedLinkedinUrl: parsedData.link,
  };
}

const setEmailType = item => {
  const itemLabel = EMAIL_TYPE_TO_LABEL[item.type];

  if (itemLabel) {
    return itemLabel;
  }

  if (EMAIL_TYPE_TO_LABEL[item.label]) {
    return EMAIL_TYPE_TO_LABEL[item.label];
  }

  if (item.is_revealed && item.is_work_email === null) {
    return EMAIL_TYPE_TO_LABEL.email;
  }

  return item.is_work_email ? EMAIL_TYPE_TO_LABEL.work : EMAIL_TYPE_TO_LABEL.direct;
};

function getContactLocation(location, contactLocation) {
  if (location) {
    return location;
  }

  if (contactLocation) {
    return `${contactLocation.city ? `${contactLocation.city}, ` : ''}${contactLocation.country}`;
  }

  return '';
}

const getEmailConfidence = qualityScore => {
  if (!qualityScore) {
    return null;
  }

  if (qualityScore > 75) {
    return 'A+';
  }

  return 'A';
};

function parseEmailData(email, originalEmail) {
  if (!email) {
    return {};
  }

  return {
    value: email.address || email.email || '',
    type: setEmailType(email),
    isRevealed: email.is_revealed || email.isShown || email.status === 'shown',
    isEdited: email.isEdited || email.is_edited,
    isManuallyAdded: email.isManuallyAdded || email.is_manually_added,
    feedback: email.feedback ?? null,
    emailConfidence: getEmailConfidence(email.quality_score),
    id: email.id || originalEmail?.id,
    isGuessed: email.is_guessed || originalEmail?.isGuessed,
    shownDate: email.shown_date || originalEmail?.shown_date,
    status: email.status || originalEmail?.status,
    shown_by: email.shown_by,
  };
}

function parsePhoneData(phone, defaultLabel, origianlPhone) {
  if (!phone) {
    return {};
  }

  return {
    value: phone.text || phone.phone || phone.number || '-',
    type: phone.type || defaultLabel,
    isEdited: phone.isEdited || phone.is_edited,
    isManuallyAdded: phone.isManuallyAdded,
    doNotCall: phone.do_not_call || null,
    isDncPhone: phone.is_do_not_call || false,
    feedback: phone.feedback ?? null,
    id: phone.id || origianlPhone?.id,
    status: phone.status || origianlPhone?.status,
    shownDate: phone.shown_date || origianlPhone?.shown_date,
    shownBy: phone.shown_by || origianlPhone?.shown_by,
    isRevealed: phone.is_revealed || origianlPhone?.is_revealed || phone.isShown || phone.status === 'shown',
    shown_by: phone.shown_by,
  };
}

function setWishlistCompanyData(company) {
  // Handle both MST snapshot (camelCase) and API response (snake_case)
  const headquarter = company?.headquarter || company?.location || {};
  const { city = '', region = '', country = '' } = headquarter;

  // Handle both property name formats for company size
  const companySize = company?.company_size || { min: company?.min, max: company?.max };
  const min = companySize?.min || 0;
  const max = companySize?.max || 0;

  const domains = company?.domains || {};
  const social = company?.social || {};
  const industry = company?.industry || {};
  const revenueRange = company?.revenue_range || company?.revenueRange;

  return {
    id: 'wishlist',
    name: company?.name || '',
    hasCompany: !!company,
    city,
    region,
    country,
    min,
    max,
    // Handle both 'about' (MST) and 'description' (API)
    about: company?.about || company?.description || '',
    // Handle both 'logoUrl' (MST) and 'logo_url' (API)
    logoUrl: company?.logoUrl || company?.logo_url || '',
    // Handle both 'domain' (MST) and 'domains.homepage' (API)
    domain: company?.domain || domains?.homepage || 'Company Website',
    // Handle both 'homePageUrl' (MST) and 'homepage_url' (API)
    homePageUrl: company?.homePageUrl || company?.homepage_url || '',
    founded: company?.founded ? company.founded.toString() : '',
    // Handle both 'specialities' (MST) and 'industry_tags' (API)
    specialities: company?.specialities || company?.industry_tags || [],
    // Handle both 'mainIndustry' (MST) and nested API structure
    mainIndustry: company?.mainIndustry || industry?.primary_industry?.key || '',
    facebook: company?.facebook || social?.facebook || '',
    twitter: company?.twitter || social?.twitter || '',
    LinkedIn: company?.LinkedIn || social?.LinkedIn || social?.linkedin || '',
    companyResultString: company?.companyResultString || JSON.stringify(company),
    revenueRange: revenueRange ? {
      ...(typeof revenueRange === 'object' ? revenueRange : {}),
      string: getRevenueRangeString(revenueRange),
    } : {},
    // Handle both 'companyLid' (MST) and 'company_lid' (API)
    companyLid: company?.companyLid || company?.company_lid || '',
    isBlockedForShow: company?.isBlockedForShow || false,
    isSocial: company?.isSocial || !!company?.social,
  };
}

function buildDataPointShowRequest(self, platform, pageUrl, isUseBulk) { // single show
  const mappedPlatform = SAVE_CONTACT_SUB_PLATFORMS[platform] || SUB_PLATFORM.LINKEDIN_MAIN;

  return {
    request_id: self.requestId,
    mask_id: self.maskedId,
    is_onboarding: self.isOnboarding,
    contacts: self.dataPointIdsToShow,
    metadata: {
      source_product: mappedPlatform,
      source_platform: PLATFORM.PLUGIN,
      url: pageUrl,
    },
    use_bulk_credits: isUseBulk,
  };
}

const Actions = self => {
  let initialState;

  const resetState = () => {
    const isSearchByDataPointEnabled = getRoot(self)?.user?.isSearchByDataPointEnabled;
    const selectedDataPointType = isSearchByDataPointEnabled ? BUTTON_TYPES.NONE : BUTTON_TYPES.EMAIL_AND_PHONE;
    applySnapshot(self, { ...initialState, selectedDataPointType });
  };

  const onSearchStatusChange = searchStatus => {
    const { postMessage: setSearchResult } = communicator.getMessenger('SEARCH_RESULT');
    const { postMessage: openFrame } = communicator.getMessenger('OPEN');
    const { totalDataCollected } = self;
    const { userSettings, context } = getRoot(self);
    const selectors = getVersionedSelectors(userSettings.externalPluginVersion, context.contextTypeName);

    logger.info('SEARCH STATUS: ', searchStatus);

    switch (searchStatus) {
      case SEARCH_STATUS.RESOLVED: {
        const dataExist = totalDataCollected === 0 ? SEARCH_RESULTS.MAX_SEARCH_RESULT : totalDataCollected;
        const shouldOpenPlugin = (dataExist === SEARCH_RESULTS.MAX_SEARCH_RESULT || dataExist > 0) && userSettings.isAutoOpen;

        setSearchResult({ isRevealed: self.isRevealed, count: dataExist });

        if (shouldOpenPlugin) {
          openFrame({ openType: 'autoOpen' });
        }
        if (self.hasDncPhone) {
          reportDncContactViewedClientEvent({
            uniqueSearchId: self.uniqueSearchId,
            subPlatform: userSettings.platform,
            pageUrl: context.url,
            numOfContactsWithDncPhone: 1,
            numOfContactsWithOnlyDncPhones: self.hasOnlyDncPhones ? 1 : 0,
            isBulkSearch: false,
          });
        }

        context.setSessionExpired(false);
        break;
      }
      case SEARCH_STATUS.MAX_SEARCHES_REACHED:
      case SEARCH_STATUS.PARSER_ERROR:
        setSearchResult({ count: SEARCH_RESULTS.MAX_SEARCH_RESULT });
        break;

      case SEARCH_STATUS.REJECTED:
        setSearchResult({ count: -1, pageListeners: selectors[userSettings.platform] });
        break;

      case SEARCH_STATUS.RETRYING_SEARCH:
        setSearchResult({ count: 0, pageListeners: selectors[userSettings.platform] });
        break;

      default:
        break;
    }
  };

  return {
    afterCreate: () => {
      initialState = getSnapshot(self);

      reaction(
        () => self.searchStatus,
        onSearchStatusChange
      );
    },
    setWishlistAddedData: data => {
      self.wishlistData.emails = data.emails;
      self.wishlistData.phones = data.phones;
      self.wishlistData.email1 = data.emails[0] || null;
      self.wishlistData.email2 = data.emails[1] || null;
      self.wishlistData.phone1 = data.phones[0] || null;
      self.wishlistData.phone2 = data.phones[1] || null;
    },
    setContactEditableData: ({ emails, phones, feedback, jobTitle }) => {
      self.email1 = parseEmailData(emails[0], self.email1);
      self.email2 = parseEmailData(emails[1], self.email2);
      self.phone1 = parsePhoneData(phones[0], 'Phone', self.phone1);
      self.phone2 = parsePhoneData(phones[1], 'Phone', self.phone2);

      if (jobTitle) {
        self.title = jobTitle.title ?? '';
        self.editedFields = jobTitle.editedFields ?? [];
      }

      if (feedback) {
        self.email1.feedback = feedback[self.email1.value] ?? null;
        self.email2.feedback = feedback[self.email2.value] ?? null;
        self.phone1.feedback = feedback[self.phone1.value] ?? null;
        self.phone2.feedback = feedback[self.phone2.value] ?? null;
      }
    },
    setWishlistContact: wishlistContact => {
      const { company } = wishlistContact;
      const defaultList = wishlistContact?.listId ? [wishlistContact.listId] : [];

      // Normalize email structure (handle both 'value' and 'address' properties)
      const normalizeEmail = email => {
        if (!email) {
          return null;
        }

        return {
          value: email.value || email.address || '',
          type: email.type || 'work',
        };
      };

      // Normalize phone structure (handle 'value', 'text', and 'number' properties)
      const normalizePhone = phone => {
        if (!phone) {
          return null;
        }

        return {
          value: phone.value || phone.text || phone.number || '',
          type: phone.type || 'work',
        };
      };

      const normalizedEmails = (wishlistContact.emails || []).map(normalizeEmail);
      const normalizedPhones = (wishlistContact.phones || []).map(normalizePhone);

      self.contactId = wishlistContact.id;
      self.wishlistData = {
        hasCompany: true,
        id: wishlistContact.id,
        firstName: wishlistContact.firstName || '',
        lastName: wishlistContact?.lastName || '',
        jobTitle: wishlistContact.jobTitle?.title || wishlistContact.jobTitle || '',
        phones: normalizedPhones,
        emails: normalizedEmails,
        email1: normalizedEmails[0] || null,
        email2: normalizedEmails[1] || null,
        phone1: normalizedPhones[0] || null,
        phone2: normalizedPhones[1] || null,
        company: setWishlistCompanyData(company),
        integrations: Array.isArray(wishlistContact.integrations) ? {} : wishlistContact.integrations,
        lists: wishlistContact.contactLists || defaultList,
      };
      self.isUserContactOwner = true;
      self.tags = wishlistContact.contactTags || [];
    },

    getContactOwnerId: (emails, phones) => {
      const emailDate = emails?.[0]?.shownDate;
      const phoneDate = phones?.[0]?.shownDate;

      if (!emailDate) {
        return phones?.[0]?.shown_by?.user_id || null;
      }

      if (!phoneDate) {
        return emails?.[0]?.shown_by?.user_id || null;
      }

      return emailDate < phoneDate
        ? emails?.[0]?.shown_by?.user_id
        : phones?.[0]?.shown_by?.user_id;
    },

    setContact: contactData => {
      const {
        name,
        emails = [],
        phones = [],
        jobTitles = [],
        contact,
        requestId,
        ordinalId = -1,
        socialLink,
        contactId: contactSearchId,
        maskedContactId,
        location,
        contactLocation,
        isBlockedForShow,
        contactSocialLinks,
        revealedBy,
        isMyContact,
        userId,
        previousJob,
        isRevealed,
        canShowContact,
        maskId,
        isPressShow,
        isOnDemandContactFound = false,
        personId,
        isFreshData = false,
        freshDataSummary = null,
        uniqueSearchId,
        isAvailableForUnshownSave,
      } = contactData;
      const isSearchByDataPointEnabled = getRoot(self)?.user?.isSearchByDataPointEnabled;
      const {
        showDate = '', lists = '', savedIntegrations = {}, feedback, tags,
      } = contact || {};
      let {
        id: contactId = '',
      } = contact || {};
      // id = searchId, contactId = showId

      const [contactDepartments] = jobTitles;
      // If the acquire status is complete, the plugin behaves the same for all contacts.
      const acquireStatus = (contactData.acquireStatus || ACQUIRE_STATUS.complete);
      if (contactData.wishedContact?.length > 0) {
        const [wishlistData] = contactData.wishedContact;
        contactId = wishlistData.id;
        self.setWishlistContact(wishlistData);
      } else {
        self.wishlistData = null;
      }

      // check for personId (not exist in show response)
      if (personId) {
        self.personId = personId;
      }

      if (envConfig.FEATURES.freshDataEnabled) {
        self.isFreshData = isFreshData;
        self.freshDataSummary = freshDataSummary;
      }

      self.isAvailableForUnshownSave = isAvailableForUnshownSave;

      if (isAvailableForUnshownSave) {
        self.contactList = lists?.length > 0 ? lists.split(',').pop() : '';
      }
      self.isPressShow = isPressShow || false;
      self.maskedContactId = maskedContactId;
      self.maskedId = maskId || null;
      self.requestId = requestId || self.requestId;
      self.ordinalId = ordinalId;
      self.showDate = showDate || '';
      self.contactId = contactId;
      self.contactSearchId = contactSearchId;
      self.contactList = self.contactList ?? (lists.length > 0 ? lists.split(',').pop() : '');
      self.fullName = name || '';
      self.isChecked = false;
      self.canShowContact = canShowContact;
      self.acquireStatus = acquireStatus;
      if (acquireStatus === ACQUIRE_STATUS.complete) {
        self.setContactEditableData({
          emails,
          phones,
          feedback,
          jobTitle: jobTitles?.[0],
          isSearchByDataPointEnabled,
        });
      } else {
        self.uuid = contactData.uuid;
        self.contactHash = contactData.contactHash;
      }

      self.setIntegrationsData(savedIntegrations);
      self.setSavedDatapoints(savedIntegrations);
      self.searchStringResult = JSON.stringify({ ...contactData, contactId }) || '';
      self.socialLink = socialLink || contactSocialLinks?.linkedin || '';
      self.location = getContactLocation(location, contactLocation);
      self.locationCode = contactData.contactLocation?.country_iso2;
      self.department = (contactDepartments?.departments?.[0] || contactDepartments?.department?.[0]) ?? '';
      self.seniority = contactDepartments?.seniority ?? '';
      self.isRevealed = (!self.isOnboarding && (!!self.showDate)) || (!self.isOnboarding && (isRevealed)); // OR /* all contact data are revealed*/
      self.tags = tags || [];
      self.isBlockedForShow = isBlockedForShow || false;
      self.revealedBy = revealedBy || null;
      self.isMyContact = isMyContact || null;
      self.userId = userId || null;
      self.previousJob = previousJob;
      self.isOnDemandContactFound = isOnDemandContactFound;
      self.isUserContactOwner = true;
      if (uniqueSearchId) {
        self.uniqueSearchId = uniqueSearchId;
      }
    },
    setSearchV2Contact: contact => {
      const {
        id,
        fullName,
        emails,
        phones,
        jobTitle,
        location,
        isBlockedForShow,
        isShown,
        tags,
        contactId,
        listId,
        socialLink,
        showDate,
        integrations,
        state,
        changes,
        wishlistContact,
        ordinalId = -1,
        previousJob,
        canShowContact,
        maskedContactId,
        isOnDemandContactFound = false,
        requestId,
        isFromDetailsFoundTab = false,
        maskedId = '',
        storeCompanyKey = null,
        feedback,
        personId = null,
        isFreshData = false,
        freshDataSummary = null,
        uniqueSearchId,
        isAvailableForUnshownSave,
        firstName,
        isPartialProfile,
        company = null,
      } = contact;
      self.setContactEditableData({ emails, phones, jobTitle, feedback });
      self.personId = personId;
      self.isPartialProfile = isPartialProfile;
      self.searchStringResult = JSON.stringify(contact);
      self.fullName = fullName ?? '';
      self.isBlockedForShow = isBlockedForShow;
      self.isRevealed = isShown;
      self.location = location?.country;
      self.seniority = jobTitle?.seniority;
      self.department = jobTitle?.department?.[0] || jobTitle?.departments?.[0];
      self.socialLink = socialLink;
      self.ordinalId = ordinalId;
      self.previousJob = previousJob;
      self.canShowContact = canShowContact;
      self.maskedContactId = maskedContactId;
      self.isOnDemandContactFound = isFromDetailsFoundTab || isOnDemandContactFound;
      self.isFromDetailsFoundTab = isFromDetailsFoundTab;
      self.maskedId = maskedId;
      self.isAvailableForUnshownSave = isAvailableForUnshownSave;
      self.firstName = firstName;
      self.storeCompanyKey = storeCompanyKey;
      if (uniqueSearchId) {
        self.uniqueSearchId = uniqueSearchId;
      }

      if (requestId) {
        self.requestId = requestId;
      }

      if (envConfig.FEATURES.freshDataEnabled) {
        self.isFreshData = isFreshData;
        self.freshDataSummary = freshDataSummary;
      }

      if (state) {
        self.state = state;
      }

      if (isAvailableForUnshownSave) {
        self.contactList = listId?.length > 0 ? listId.split(',').pop() : '';
        self.setIntegrationsData(integrations);
      }

      self.contactId = contactId || id;

      if (isShown) {
        self.contactId = contactId || '';
        // list ids are chained with commas, e.g: "all,f32c2d18-09ec-40f4-b048-77a3549a9cb1"
        self.contactList = listId?.length > 0 ? listId.split(',').pop() : '';
        self.showDate = showDate || '';
        self.tags = tags;
        self.setIntegrationsData(integrations);
        self.setSavedDatapoints(integrations);
        if (changes?.length) {
          self.changes = changes;
        }
      } else if (wishlistContact) {
        self.setWishlistContact(wishlistContact);
      } else if (listId && listId.length > 0 && isPartialProfile && !isShown) {
        self.contactList = listId?.length > 0 ? listId.split(',').pop() : '';

        self.setWishlistContact({
          ...contact,
          id: contact.contactId, // Explicitly use contactId as the id field
          listId: self.contactList,
          company,
        });
      }
      self.isUserContactOwner = true;
    },

    resetFreshData: () => {
      self.isFreshData = false;
      self.freshDataSummary = null;
    },

    setIntegrationsData: savedIntegrations => {
      Object.entries(savedIntegrations).forEach(([key, { link, type, outdatedSince, lastUpdated, errors }]) => {
        if (!errors?.length && link) {
          self.integrationsData.set(key, {
            link,
            type,
            ...(outdatedSince && { outdatedSince }),
            ...(lastUpdated && { lastUpdated }),
          });
        }
      });
    },

    setIntegrationByKey: (integrationKey, { link, lastUpdated, outdatedSince }) => {
      const integration = self.integrationsData.get(integrationKey);
      if (integration) {
        integration.link = link;
        integration.lastUpdated = lastUpdated;
        integration.outdatedSince = outdatedSince;
      } else {
        self.integrationsData.set(integrationKey, { link, lastUpdated, outdatedSince });
      }
    },

    setSavedDatapoints: integrations => {
      Object.values(integrations).forEach(({ dataPoints }) => {
        if (!dataPoints || !dataPoints.length) {
          return;
        }

        dataPoints.forEach(datapointId => {
          if (!self.savedDatapoints.includes(datapointId)) {
            self.savedDatapoints.push(datapointId);
          }
        });
      });
    },

    refreshData: flow(function* refreshData() {
      const payload = {
        requestId: self.requestId,
        contacts: [self.contactId],
      };
      try {
        const response = yield refreshContactData(payload);
        const contactReponse = response[self.contactId];
        if (!contactReponse.isSuccess) {
          throw new Error(`Could not refresh contact id: ${self.contactId}`);
        }

        self.setSearchV2Contact(contactReponse.contact);
      } catch (e) {
        logger.error('Failed to refresh contact', e);
      }
    }),

    search: flow(function* search(data, forceSearch = false) {
      const rootStore = getRoot(self);
      const { userSettings: { isPluginOpen }, app, user, onDemand } = rootStore;
      const { postMessage: setSearchResult } = communicator.getMessenger('SEARCH_RESULT');

      if (!self.isEmpty && !onDemand.isOnDemandContactShown) {
        logger.info('Search already retrieve result');
        self.searchStatus = SEARCH_STATUS.RESOLVED;
        setSearchResult({ count: SEARCH_RESULTS.MAX_SEARCH_RESULT });

        return;
      }

      if (searchTries >= MAX_SEARCH_TRIES && !forceSearch) {
        logger.info(`Reached ${MAX_SEARCH_TRIES} searches for current profile`);
        self.searchStatus = SEARCH_STATUS.MAX_SEARCHES_REACHED;

        return;
      }

      searchTries += 1;
      self.searchStatus = SEARCH_STATUS.SEARCHING;
      logger.info(`Initiating search try number ${searchTries} for current profile`);
      let searchResult;
      setPlatformByParserSourcePlatform(data, rootStore);
      try {
        const searchData = {
          request_id: self.requestId,
          firstSearch: self.firstSearch,
          sessionId: app.sessionId,
          ...data,
        };

        const mappedSearchData = mapSingleSearchData(searchData, Boolean(isPluginOpen));

        const searchContactByDataPointResponse = yield contactDataPointSearch(mappedSearchData);
        searchResult = searchContactByDataPointResponse?.data;
      } catch (e) {
        // If not maxed on search tries and error is parsing error - retry search
        if ((searchTries >= MAX_SEARCH_TRIES && !forceSearch) || e.code !== 422) {
          self.searchStatus = SEARCH_STATUS.MAX_SEARCHES_REACHED;
        } else {
          self.searchStatus = SEARCH_STATUS.RETRYING_SEARCH;
        }

        return;
      }

      if (searchResult) {
        const { isOnboarding, maskId } = searchResult.metadata;
        self.isOnboarding = isOnboarding;
        self.maskedId = maskId;
        if (isOnboarding) {
          reportClientEvent({
            eventType: 'user_onboarding_demo_search',
            eventValue: user.industry,
          }, { enrichData: false });
        }
        self.firstSearch = false;

        const contact = searchResult.contacts[0];
        const { wishlistContact, uniqueSearchId = '' } = contact;
        const company = wishlistContact ?
          { ...wishlistContact.company, company_lids: searchResult?.companies?.[0]?.company_lids }
          : searchResult.companies[0] || null;

        self.company.setCompany(company);
        self.setSearchV2Contact({ ...contact, maskedId: maskId, company: company ?? null });
        self.searchStatus = SEARCH_STATUS.RESOLVED;

        onDemand.setIsOnDemandContactShown(false);
        self.uniqueSearchId = uniqueSearchId;
        app?.setUniqueSearchId(uniqueSearchId);

        return;
      }

      self.searchStatus = SEARCH_STATUS.REJECTED;
    }),

    revealContact: flow(function* revealContact() {
      const { userSettings, context, onDemand } = getRoot(self);
      if (self.isFromDetailsFoundTab && context.type === 'PROFILE') {
        //   This is for the extreme case, that the user has an on-demand contact,
        //   and the user is on that specific contact profile on linkedin.
        //   In case of show from history tab, trigger new search request, from prospect tab
        //  */
        onDemand.setIsOnDemandContactShown(true);
      }
      const isUseBulk = sessionStorage.getItem(USE_BULK_KEY);
      const showPayload = buildDataPointShowRequest(self, userSettings.platform, context.url, !!isUseBulk);
      const numberOfCreditsCharged = sumNumberOfCreditsCharge(showPayload?.contacts || []);
      const showResult = yield showContactsByDataPoints(showPayload);
      const response = showResult?.data.data.contacts.request[0];

      return { showResult, response, numberOfCreditsCharged };
    }),

    show: flow(function* show() {
      if (self.isOnboarding && !!self.showDate) {
        self.isRevealed = true;
        self.isPressShow = true;
      } else if (self.acquireStatus === ACQUIRE_STATUS.none) {
        self.isLoading = true;
        const acquiredContactsResult = yield acquireContacts({
          requestId: self.requestId,
          platform: BULK_SEARCH_PLATFORM,
          contacts: [self.contactHash],
        });
        if (acquiredContactsResult?.length) {
          self.setAcquireStatus(ACQUIRE_STATUS.pending);
        }
        self.isLoading = false;
      } else {
        self.isLoading = true;

        try {
          const { showResult, response, numberOfCreditsCharged } = yield self.revealContact();
          if (envConfig.FEATURES?.NOTIFY_EMAIL_ON_DEMAND && (self.isFromDetailsFoundTab || self.isOnDemandContactFound)) {
            sendClientMetric(buildOnDemandShowClientMetricPayload(false, self.isFromDetailsFoundTab, self.isOnDemandContactFound));
          }

          if (response && showResult?.data?.type === 'success') {
            self.setContact({ ...response, isFreshData: self.isFreshData, freshDataSummary: self.freshDataSummary, previousJob: self.previousJob, isFromDetailsFoundTab: self.isFromDetailsFoundTab, isOnDemandContactFound: self.isOnDemandContactFound });
            self.isRevealed = true;
            self.isPressShow = true;

            if (!self.isOnboarding && showResult.enterprise295Consumption?.regular > 0) {
              getRoot(self).user.onCreditUsed();
            }

            const root = getRoot(self);
            if (root.userActivity) {
              root.userActivity.checkAndShowRateModal(RATE_MODAL_TRIGGER_ACTIONS.CONTACT_REVEAL);
              root.userActivity.reportContactsRevealed({
                action: RATE_MODAL_TRIGGER_ACTIONS.CONTACT_REVEAL,
                contacts: [response?.contact?.id],
              });
            }
            if (root.fetchAccountCredits) {
              root.fetchAccountCredits(numberOfCreditsCharged);
            }
          }
          self.isLoading = false;
          showResult.numberOfCreditsCharged = numberOfCreditsCharged;

          return showResult;
        } catch (e) {
          self.isLoading = false;

          // for some reason, bulk error object gets here as a string, with no data
          if (self.isFromDetailsFoundTab && e === OUT_OF_CREDITS_BULK_ERROR_MESSAGE) {
            return OUT_OF_CREDITS_BULK_ERROR_OBJECT;
          }

          return { errorCode: e.statusCode ?? e.code, message: e.message, data: e?.data };
        }
      }

      return self;
    }),
    saveToWishlist: flow(function* saveToWishlist({
      contactCompany,
      contactFirstName,
      contactLastName,
      contactTitle,
      contactEmail,
      contactPhone,
      listIdToSave = '',
    }) {
      const { context, userSettings } = getRoot(self);
      const { url, parserResult } = context;
      const { platform } = userSettings;

      const {
        parsedFullName,
        parsedCompanyName,
        parsedLinkedinUrl,
      } = getParsedData(parserResult, self);

      const data = getContactWishlistData({
        contactCompany,
        contactFirstName,
        contactLastName,
        contactTitle,
        contactEmail,
        contactPhone,
        parsedDataFullName: parsedFullName,
        parsedDataCompany: parsedCompanyName,
        parsedLinkedinUrl,
        url,
        platform: SAVE_CONTACT_SUB_PLATFORMS[platform] || SUB_PLATFORM.LINKEDIN_MAIN,
      });
      try {
        const response = yield saveContactToWishlist(data);
        self.setWishlistContact({ ...response?.wishedContacts[0], listId: listIdToSave ?? '' });

        return response;
      } catch (error) {
        return null;
      }
    }),

    undoSave: () => {
      self.setContactList = '';
      self.integrationsUrl.clear();
    },

    setContactList: newList => {
      self.contactList = newList;
      if (self.isWishlistContact) {
        self.wishlistData.lists = [...self.wishlistData.lists, newList];
      }
    },

    setContactIntegrationData: (integrationKey, data) => {
      self.setIntegrationByKey(integrationKey, data);
      if (self.isWishlistContact) {
        self.wishlistData.integrations.set(integrationKey, { link: data?.link });
      }
    },

    setFeedback: (key, feedbackValue) => {
      self[key].feedback = feedbackValue;
    },

    setIsPressShow: value => {
      self.isPressShow = value;
    },

    setIsChecked: value => {
      self.isChecked = value;
    },
    setSearchStatus: value => {
      self.searchStatus = value;
    },
    setAcquireStatus: value => {
      self.acquireStatus = value;
    },
    onTagsChanged: ({ tags }) => {
      self.tags = [...tags];
    },
    setUniqueSearchId: uniqueSearchId => {
      self.uniqueSearchId = uniqueSearchId;
    },
    setSequenceNum: sequenceNum => {
      self.sequenceNum = sequenceNum;
    },
    setShowDataPointType: type => {
      self.selectedDataPointType = type;
    },
    setCreditSplitButtonId: id => {
      self.creditSplitClickedButtonId = id;
    },
    setShowCreditSplitPopup: (isOpen, type) => {
      self.showCreditSplitPopup = isOpen;
      self.creditSplitErrorType = type;
    },
    init: type => {
      if (type === 'UNKNOWN') {
        self.searchStatus = SEARCH_STATUS.IDLE;
      } else {
        resetState();
        searchTries = 0;
        // generate new request id only when url changed
        self.requestId = uuid();
      }
    },
  };
};

export default Actions;
